import { useEffect, useState } from "react";
import { ADS_CONFIG, isAdsenseConfigured } from "@/lib/adsConfig";
import { hasAdvertisingConsent, onConsentChanged } from "@/lib/cookieConsent";

// Module-level (not component-level) so it survives across every mount of this
// component — there is exactly one of these mounted globally (see App.js, same pattern
// as BusinessSeo/BusinessQualityLayer), but this guard also protects against the
// unlikely case of double-mounting (e.g. React StrictMode's dev double-invoke) ever
// injecting the Google script twice (§81 — one central loader, never duplicated).
let scriptLoadPromise = null;

function loadAdsenseScript(clientId) {
  if (document.querySelector('script[data-mysitecare-adsense="true"]')) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(clientId)}`;
    script.dataset.mysitecareAdsense = "true";
    script.onload = () => resolve();
    script.onerror = () => { scriptLoadPromise = null; reject(new Error("Failed to load the AdSense script")); };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Mounted once globally (App.js). Loads Google's AdSense script using the
 * Google-recommended async model — and only when: this is a real production build,
 * AdSense is actually configured (§19 — never a fabricated client ID), and the visitor
 * has given advertising consent (§33). Never loads on its own from development or test
 * environments, matching AdSlot's own placeholder-only behavior there.
 */
export default function AdSenseScriptLoader() {
  const [consented, setConsented] = useState(hasAdvertisingConsent);

  useEffect(() => onConsentChanged(() => setConsented(hasAdvertisingConsent())), []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return; // never touch the network outside a real production build
    if (!isAdsenseConfigured() || !consented) return;
    loadAdsenseScript(ADS_CONFIG.clientId).catch(() => {
      // Network/ad-blocker failure — AdSlot's own try/catch around the push() call
      // already tolerates the script never having loaded; nothing further to do here.
    });
  }, [consented]);

  return null;
}
